import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import type { Contract } from '@shared/types';

interface ContractListProps {
  contracts: Contract[];
  loading: boolean;
  onRefresh: () => void;
}

const statusColors: Record<string, string> = {
  draft: 'bg-yellow-500',
  review: 'bg-blue-500',
  signature: 'bg-purple-500',
  executed: 'bg-green-500',
  archived: 'bg-gray-400',
};

const statusLabels: Record<string, string> = {
  draft: 'DRAFT',
  review: 'IN REVIEW',
  signature: 'SIGNATURE',
  executed: 'EXECUTED',
  archived: 'ARCHIVED',
};

// Status sort priority (lower number = higher priority)
const statusPriority: Record<string, number> = {
  draft: 1,
  review: 2,
  signature: 3,
  executed: 4,
  archived: 5,
};

export function ContractList({ contracts, loading, onRefresh }: ContractListProps) {
  const navigate = useNavigate();

  // Sort contracts: primary by status priority, secondary by client name
  const sortedContracts = [...contracts].sort((a, b) => {
    // Primary sort: status priority
    const statusA = statusPriority[a.status] || 999;
    const statusB = statusPriority[b.status] || 999;
    
    if (statusA !== statusB) {
      return statusA - statusB;
    }
    
    // Secondary sort: client name (alphabetical)
    return a.clientName.localeCompare(b.clientName);
  });

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading contracts...</p>
      </div>
    );
  }

  if (contracts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-12 text-center">
        <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">No contracts found</h3>
        <p className="mt-2 text-gray-600">
          Try adjusting your filters or create a new contract to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          {contracts.length} Contract{contracts.length !== 1 ? 's' : ''}
        </h2>
        <button
          onClick={onRefresh}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-md"
        >
          <ArrowPathIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="divide-y divide-gray-200">
        {sortedContracts.map((contract) => (
          <div
            key={contract.id}
            onClick={() => navigate(`/contracts/${contract.id}`)}
            className="relative p-6 pr-12 hover:bg-gray-50 cursor-pointer transition-colors overflow-hidden"
          >
            {/* Vertical Status Tab - Full Height */}
            <div className={`absolute right-0 top-0 bottom-0 w-8 flex items-center justify-center ${
              statusColors[contract.status] || 'bg-gray-400'
            }`}>
              <div
                style={{
                  transform: 'rotate(-90deg)',
                  transformOrigin: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                <span className="text-xs font-bold text-white tracking-wider">
                  {statusLabels[contract.status] || contract.status.toUpperCase()}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div>
                    {contract.title && (
                      <h3 className="text-lg font-semibold text-gray-900">
                        {contract.title}
                      </h3>
                    )}
                    <p className={`text-gray-700 ${contract.title ? 'text-sm' : 'text-lg font-semibold'}`}>
                      {contract.clientName}
                    </p>
                  </div>
                  {contract.templateType && (
                    <span className="px-2 py-1 text-xs font-bold rounded-full bg-primary-100 text-primary-700 border border-primary-300">
                      {contract.templateType.toUpperCase()}
                    </span>
                  )}
                </div>
                {contract.executiveSummary && (
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                    {contract.executiveSummary}
                  </p>
                )}
                <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                  <span>
                    Created: {new Date(contract.createdAt).toLocaleDateString()}
                  </span>
                  {contract.expirationDate && (
                    <span>
                      Expires: {new Date(contract.expirationDate).toLocaleDateString()}
                    </span>
                  )}
                  <span>
                    {contract.deliverables?.length || 0} deliverable(s)
                  </span>
                  <span>
                    {contract.payments?.length || 0} payment(s)
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

